<template>
  <div class="warehouse-select-dialog-wrapper">
    <v-dialog
      fullscreen
      v-model="isDialogOpen"
      transition="slide-y-reverse-transition"
      aria-labelledby="warehouse-selection-title">
      <v-card class="dialog-list">
        <v-card-title id="warehouse-selection-title" class="dialog-list__title">
          <h2 class="typography-heading-sm-bold">
            {{ t('warehouse.dialogs.selectWarehouse.buttons.changeWarehouse') }}
          </h2>
          <v-spacer></v-spacer>
          <v-btn
            icon
            variant="text"
            size="small"
            @click="isDialogOpen = false"
            :aria-label="t('global.basicTerms.close')">
            <v-icon icon="mdi-close"></v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-skeleton-loader v-if="areWarehousesLoading" type="list-item-three-line" />

          <v-list v-else class="dialog-list__list">
            <v-list-item
              v-for="warehouse in warehouses"
              :key="warehouse.id"
              @click="handleWarehouseClick(warehouse)"
              variant="plain"
              color="#C96500"
              class="dialog-list__item">
              {{ warehouse.name }}
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>

      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          class="typography-body-sm-bold text-none px-1"
          :loading="areWarehousesLoading"
          prepend-icon="mdi-map-marker-outline"
          append-icon="mdi-chevron-down">
          {{
            truncateString(
              selectedWarehouseName ||
                t('warehouse.dialogs.selectWarehouse.placeholders.selectWarehouse'),
              20
            )
          }}
        </v-btn>
      </template>
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount, ref, watch, toValue } from 'vue';
import { useWarehouseQueries, useEventHub } from '@/composables';
import { truncateString, type IWarehouse } from '@nova/core';
import { useI18n } from 'vue-i18n';

import { useWarehouseStore } from '@/store/useWarehouseStore';
import type { IWarehouseStore } from '@/store/useWarehouseStore';

const { t } = useI18n();
const isDialogOpen = ref(false);
const warehouseStore = useWarehouseStore() as unknown as IWarehouseStore;
const eventHub = useEventHub();

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

const { getListWarehousesQuery } = useWarehouseQueries();
const warehouseListQuery = getListWarehousesQuery({
  enabled: false,
  options: { fields: ['name,timezone'], joins: null }
});

const warehouses = computed(() => warehouseListQuery.data.value || []);
const selectedWarehouseId = computed(() => toValue(warehouseStore.selectedWarehouseId));
const selectedWarehouse = computed(
  () => warehouses.value.find(w => w.id === selectedWarehouseId.value) ?? null
);
const selectedWarehouseName = computed(() => selectedWarehouse.value?.name ?? null);
const areWarehousesLoading = computed(() => warehouseListQuery.isFetching.value);

const handleWarehouseClick = (warehouse?: Partial<IWarehouse>) => {
  if (!warehouse) {
    return;
  }

  warehouseStore.setSelectedWarehouse(warehouse);

  emit('update:modelValue', warehouse.id || null);
  isDialogOpen.value = false;
};

watch(warehouses, newWarehouses => {
  const firstWarehouse = newWarehouses?.[0] ?? null;
  const currentWarehouse = selectedWarehouse.value ?? null;

  if (!newWarehouses.length) {
    warehouseStore.setSelectedWarehouse(null);

    emit('update:modelValue', null);
    return;
  }

  if (selectedWarehouseId.value && currentWarehouse !== null) {
    warehouseStore.setSelectedWarehouse(currentWarehouse);

    emit('update:modelValue', currentWarehouse.id || null);
    return;
  }

  if (!selectedWarehouseId.value || !currentWarehouse) {
    if (firstWarehouse !== null) {
      warehouseStore.setSelectedWarehouse(firstWarehouse);

      emit('update:modelValue', firstWarehouse.id || null);
      return;
    }

    warehouseStore.setSelectedWarehouse(null);

    emit('update:modelValue', null);
    return;
  }
});

const handleWarehouseChange = () => {
  warehouseListQuery.refetch();
};

onMounted(() => {
  handleWarehouseChange();

  eventHub.on('delete-Warehouse', handleWarehouseChange);
  eventHub.on('create-Warehouse', handleWarehouseChange);
  eventHub.on('update-Warehouse', handleWarehouseChange);
});

onBeforeUnmount(() => {
  eventHub.off('delete-Warehouse', handleWarehouseChange);
  eventHub.off('create-Warehouse', handleWarehouseChange);
  eventHub.off('update-Warehouse', handleWarehouseChange);
});
</script>

<style lang="scss" scoped>
:deep(.v-card) {
  height: calc(100dvh - $spacing-6) !important;
  min-height: calc(100dvh - $spacing-6) !important;
  max-height: calc(100dvh - $spacing-6) !important;
  top: $spacing-6;
  margin: 0;
}
</style>
